import { Component, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  Routes,
} from '@angular/router';
import { ToasterComponent } from './core/components/toaster/toaster.component';
import { ConfirmRemoveComponent } from './core/components/confirm-remove/confirm-remove.component';
import { LoaderComponent } from './core/components/loader/loader.component';
import { ConfirmationBoxComponent } from './core/components/confirmation-box/confirmation-box.component';
import { LoaderService } from './core/services/loader.service';
import { EnvironmentService } from '../environments/environment.service';
import { ApiService } from './core/services/api.service';
import { AlertInfoComponent } from './core/components/alert-info/alert-info';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet, 
    ToasterComponent, 
    ConfirmRemoveComponent, 
    LoaderComponent, 
    ConfirmationBoxComponent,
    AlertInfoComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'af-lims';
  constructor(
    private readonly router: Router,
    private readonly loaderService: LoaderService,
    private readonly environmentService: EnvironmentService,
    private readonly apiService: ApiService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loaderService.displayLoader(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loaderService.displayLoader(false);
      }
    });
  }

  ngOnInit() {
    this.applyThemes();
    this.updateRouteConfig();
    this.apiService.updateSharedJSON();
  }

  applyThemes() {
    const body = document.body;
    if (this.environmentService.clientId === '3') {
      body.classList.add('green-theme');
    } else {
      body.classList.remove('green-theme');
    }
  }

  updateRouteConfig() {
    let newChildren: Routes = [];
    if (this.environmentService.clientId === '1') {
      newChildren = [
        {
          path: 'biological-sample-inventory/plasmids',
          loadComponent: () =>
            import('./feature/inventory/biological-sample/plasmids/plasmids.component').then(
              (comp) => comp.PlasmidsComponent,
            ),
          data: { preload: 'biological-sample-inventory' },
        },
        {
          path: 'biological-sample-inventory/bacterial-strains',
          loadComponent: () =>
            import('./feature/inventory/biological-sample/bacterial-strains/bacterial-strains.component').then(
              (comp) => comp.BacterialStrainsComponent,
            ),
          data: { preload: 'biological-sample-inventory' },
        },
        {
          path: 'biological-sample-inventory/cell-lines',
          loadComponent: () =>
            import('./feature/inventory/biological-sample/cell-lines/cell-lines.component').then(
              (comp) => comp.CellLinesComponent,
            ),
          data: { preload: 'biological-sample-inventory' },
        },
        {
          path: 'lab-equipments/refrigerator',
          loadComponent: () =>
            import('./feature/inventory/lab-equipments/refrigerator/refrigerator.component').then(
              (comp) => comp.RefrigeratorComponent,
            ),
          data: { preload: 'lab-equipment' },
        },

        {
          path: 'lab-equipments/freezer',
          loadComponent: () =>
            import('./feature/inventory/lab-equipments/freezer/freezer.component').then(
              (comp) => comp.FreezerComponent,
            ),
          data: { preload: 'lab-equipment' },
        },
        {
          path: 'lab-equipments/storage-cabinet',
          loadComponent: () =>
            import('./feature/inventory/lab-equipments/storage-cabinets/storage-cabinets.component').then(
              (comp) => comp.StorageCabinetsComponent,
            ),
          data: { preload: 'lab-equipment' },
        },
        {
          path: 'lab-equipments/shelves',
          loadComponent: () =>
            import('./feature/inventory/lab-equipments/shelves/shelves.component').then(
              (comp) => comp.ShelvesComponent,
            ),
          data: { preload: 'lab-equipment' },
        },

        {
          path: 'lab-equipments',
          loadComponent: () =>
            import('./feature/inventory/lab-equipments/lab-equipment-list/lab-equipment-list.component').then(
              (comp) => comp.LabEquipmentListComponent,
            ),
        },

        {
          path: 'instruments',
          loadComponent: () =>
            import('./feature/inventory/instruments/instruments/instruments.component').then(
              (comp) => comp.InstrumentsComponent,
            ),
          data: { preload: 'instruments' },
        },
        {
          path: 'lab-consumables/assay-kits',
          loadComponent: () =>
            import('./feature/inventory/lab-consumables/assay-kits/assay-kits.component').then(
              (comp) => comp.AssayKitsComponent,
            ),
          data: { preload: 'lab-consumables/assay-kits' },
        },
        {
          path: 'lab-consumables/cell-culture-reagents/cell-culture-media-antibiotics-agar-plates',
          loadComponent: () =>
            import('./feature/inventory/lab-consumables/cell-culture-reagents/ccm-a-a/ccm-a-a.component').then(
              (comp) => comp.CcmAAComponent,
            ),
          data: { preload: 'lab-consumables/cell-culture-reagents' },
        },
        {
          path: 'lab-consumables/cell-culture-reagents/gels-buffers-lab-reagents',
          loadComponent: () =>
            import('./feature/inventory/lab-consumables/cell-culture-reagents/g-b-lr/g-b-lr.component').then(
              (comp) => comp.GBLrComponent,
            ),
          data: { preload: 'lab-consumables/cell-culture-reagents' },
        },
        {
          path: 'lab-consumables/labwares/glasswares',
          loadComponent: () =>
            import('./feature/inventory/lab-consumables/lab-wares/glass-wares/glass-wares.component').then(
              (comp) => comp.GlassWaresComponent,
            ),
          data: { preload: 'lab-consumables/labwares' },
        },
        {
          path: 'lab-consumables/labwares/plasticwares',
          loadComponent: () =>
            import('./feature/inventory/lab-consumables/lab-wares/plastic-wares/plastic-wares.component').then(
              (comp) => comp.PlasticWaresComponent,
            ),
          data: { preload: 'lab-consumables/labwares' },
        },
      ];
    } else {
      newChildren = [
        {
          path: '',
          redirectTo: 'biological-sample-inventory/plasmids',
          pathMatch: 'full',
        },
        {
          path: '**',
          loadComponent: () =>
            import('./feature/inventory/inventory-items/inventory-items.component').then(
              (comp) => comp.InventoryItemsComponent,
            ),
        },
      ];
    }
    // 1. Get the current route configuration
    const currentRoutes: Routes = this.router.config;

    // 2. Find the parent route to which you want to add children (e.g., the 'admin' route)
    const parentRoute = currentRoutes
      .find((route) => route.path === '')
      ?.children?.find((route1) => route1.path === 'inventory');

    if (parentRoute) {
      // 3. Ensure the parent route has a 'children' array, and append the new children
      parentRoute.children = [...newChildren];

      // 4. Apply the updated configuration
      this.router.resetConfig(currentRoutes);

    } else {
      console.error('Parent route not found!');
    }
  }
}
